import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AgentConfig, AgentUpdateRequest } from "../../shared/types/agent";
import { agentApi } from "../services/agentApi";
import { AgentForm } from "../components/AgentSettings/AgentForm";

export const AgentEditView: React.FC = () => {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const [agent, setAgent] = useState<AgentConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load agent data
  useEffect(() => {
    if (agentId) {
      loadAgent();
    }
  }, [agentId]);

  const loadAgent = async () => {
    if (!agentId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await agentApi.get(agentId);
      if (response.success && response.data) {
        setAgent(response.data);
      } else {
        setError(response.error || 'Failed to load agent');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agent');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAgent = async (data: AgentUpdateRequest) => {
    if (!agentId) return;
    
    try {
      setSaving(true);
      setError(null);
      const response = await agentApi.update(agentId, data);
      if (response.success && response.data) {
        setAgent(response.data);
        navigate('/app/settings/agents');
      } else {
        setError(response.error || 'Failed to update agent');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update agent');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/app/settings/agents');
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-300">Loading agent...</p>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-4">Agent not found</div>
          <p className="text-gray-300 mb-4">The requested agent could not be found.</p>
          <button
            onClick={handleCancel}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded"
          >
            Back to Agents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b border-border-primary">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Edit Agent</h2>
              <p className="text-gray-400 mt-1">Modify agent configuration: {agent.name}</p>
            </div>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </button>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            {error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200">
                {error}
              </div>
            )}
            
            <AgentForm
              onSubmit={handleUpdateAgent}
              onCancel={handleCancel}
              loading={saving}
              initialData={agent}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
